import React, { useState, useEffect, useRef, useContext, useCallback, useLayoutEffect } from "react";
import { Box, CircularProgress, Modal, IconButton, Typography, Paper, InputBase, Button } from "@mui/material";
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { AuthContext } from "./Auth"; // Adjust path as needed
import { AppContext } from "./AppProvider"; // Adjust path as needed
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc, addDoc, serverTimestamp, updateDoc, writeBatch, startAfter, getDocs, setDoc } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';
import './MediaTextView.css';
import { createImageThumbnail } from './imageUtils';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ChatUserHeader from './ChatUserHeader';

const FullSizeImageModal = ({ open, imageUrl, onClose }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 16, right: 16, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 1400 }}
        size="large"
        aria-label="Close"
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      <img src={imageUrl} alt="full" style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: 8, boxShadow: '0 4px 32px #0008' }} />
    </Box>
  </Modal>
);

const ChatList = React.forwardRef(({ messages, currentUserId, isOwner, onImageClick, onArchive, fetchMore, hasMore, setAtTop }, ref) => {
  const listRef = useRef();
  const prevMsgCount = useRef(messages.length);
  const prevFirstMsgId = useRef(messages[0]?.id);
  const prevFirstVisibleMsgIdRef = useRef(null);
  const prevFirstVisibleMsgOffsetFromTopRef = useRef(0);
  const programmaticScrollRef = useRef(false);
  const userHasScrolledRef = useRef(false);
  const pendingScrollRestoreRef = useRef(false);

  React.useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (listRef.current) listRef.current.scrollTop = 0;
    }
  }));

  // Helper: get the first visible message's ID and offset from top
  const getFirstVisibleMsgIdAndOffsetFromTop = () => {
    if (!listRef.current) return { id: null, offset: 0 };
    const children = Array.from(listRef.current.children);
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const rect = child.getBoundingClientRect();
      const parentRect = listRef.current.getBoundingClientRect();
      if (rect.bottom > parentRect.top + 1) {
        return {
          id: child.dataset.msgid,
          offset: rect.top - parentRect.top
        };
      }
    }
    return { id: null, offset: 0 };
  };

  // Infinite scroll: load more when user scrolls to bottom (for reversed chat)
  const handleScroll = () => {
    const ref = listRef.current;
    if (!ref) return;
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      return;
    }
    if (!userHasScrolledRef.current && ref.scrollTop > 30) {
      userHasScrolledRef.current = true;
    }
    // Only trigger fetchMore if user has scrolled and is at the bottom (oldest message) and there are more messages
    if (hasMore && userHasScrolledRef.current && ref.scrollTop + ref.clientHeight >= ref.scrollHeight - 2 && !ref._loadingMore) {
      ref._loadingMore = true;
      // Record the first visible message's ID and offset from top before loading more
      const { id, offset } = getFirstVisibleMsgIdAndOffsetFromTop();
      prevFirstVisibleMsgIdRef.current = id;
      prevFirstVisibleMsgOffsetFromTopRef.current = offset;
      pendingScrollRestoreRef.current = true;
      Promise.resolve(fetchMore()).finally(() => {
        if (listRef.current) listRef.current._loadingMore = false;
      });
    }

    // In ChatList, detect if user scrolls to top (latest page)
    if (typeof setAtTop === 'function') {
      if (ref.scrollTop === 0) {
        setAtTop(true);
      } else {
        setAtTop(false);
      }
    }
  };

  useLayoutEffect(() => {
    if (!listRef.current) return;
    const added = messages.length - prevMsgCount.current;
    // Only scroll to top on new message if not paginating
    if (added === 1 && messages[0] && messages[0].id !== prevFirstMsgId.current && !pendingScrollRestoreRef.current) {
      const atTop = listRef.current.scrollTop === 0;
      if (atTop) {
        programmaticScrollRef.current = true;
        listRef.current.scrollTop = 0;
      }
      userHasScrolledRef.current = false;
    } else if (added > 1 && prevMsgCount.current > 0 && prevFirstVisibleMsgIdRef.current && pendingScrollRestoreRef.current) {
      // After loading more, restore scroll so the previously first visible message is at the same offset from the top
      const ref = listRef.current;
      const target = ref.querySelector(`[data-msgid="${prevFirstVisibleMsgIdRef.current}"]`);
      if (target) {
        programmaticScrollRef.current = true;
        const parentRect = ref.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        setTimeout(() => {
          const newParentRect = ref.getBoundingClientRect();
          const newTargetRect = target.getBoundingClientRect();
          const desiredScrollTop = ref.scrollTop + (newTargetRect.top - newParentRect.top) - prevFirstVisibleMsgOffsetFromTopRef.current;
          ref.scrollTop = desiredScrollTop;
        }, 0);
      }
      prevFirstVisibleMsgIdRef.current = null;
      prevFirstVisibleMsgOffsetFromTopRef.current = 0;
      pendingScrollRestoreRef.current = false;
    }
    prevMsgCount.current = messages.length;
    prevFirstMsgId.current = messages[0]?.id;
  }, [messages.length]);

  return (
    <Box ref={listRef} onScroll={handleScroll} className="message-list-scroll" sx={{ flex: 1, overflowY: 'auto', p: 1, display: 'flex', flexDirection: 'column' }}>
      {messages.map(msg => {
        const isImageOnly = (
          msg.attachments &&
          msg.attachments.length === 1 &&
          (msg.attachments[0].type === 'image' || msg.attachments[0].type === 'gif') &&
          (!msg.text || msg.text.replace(/[\uFFFC\u2009\s]/g, '') === '')
        );
        return (
          <Box key={msg.id} sx={{ mb: 0.5 }}>
            {/* Sender avatar and name OUTSIDE the message box */}
            <ChatUserHeader senderId={msg.senderId} />
            <Paper data-msgid={msg.id} sx={{
              mb: 2, // increased gap between messages
              p: isImageOnly ? 0 : 1,
              background: isImageOnly ? 'transparent' : 'rgba(128,128,128,0.1)',
              position: 'relative',
              display: 'inline-block',
              maxWidth: '90%',
              minWidth: 48,
              borderRadius: 6,
              boxShadow: isImageOnly ? 'none' : 1,
              width: 'fit-content',
            }}>
              {isOwner && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onArchive(msg.id)}
                  sx={{ position: 'absolute', top: -12, right: -12, zIndex: 2, p: '2px', background: '#fff', boxShadow: 1 }}
                  aria-label="Archive"
                >
                  <RemoveCircleIcon fontSize="small" />
                </IconButton>
              )}
              <MessageContent text={msg.text} attachments={msg.attachments} onImageClick={onImageClick} />
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
});

// Helper to render mixed text and inline images
function MessageContent({ text, attachments, onImageClick }) {
  // If only one attachment, type image/gif, and text is empty or only object replacement chars/whitespace, show full image
  const isImageOnly = (
    attachments &&
    attachments.length === 1 &&
    (attachments[0].type === 'image' || attachments[0].type === 'gif') &&
    (!text || text.replace(/[\uFFFC\u2009\s]/g, '') === '')
  );
  if (isImageOnly) {
    const fullUrl = attachments[0].url?.split('|')[1] || attachments[0].url;
    return (
      <Box sx={{ display: 'block', width: '100%' }}>
        <img
          src={fullUrl}
          alt="full"
          className="chat-full-image"
          onClick={() => onImageClick(fullUrl)}
        />
      </Box>
    );
  }
  if (!attachments || attachments.length === 0) return <Typography variant="body2">{text}</Typography >;
  // Build a list of segments: text and images, based on object replacement char (\uFFFC)
  const segments = [];
  let lastIdx = 0;
  let imgIdx = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\uFFFC') {
      if (lastIdx < i) segments.push({ type: 'text', value: text.slice(lastIdx, i) });
      if (attachments[imgIdx]) segments.push({ type: 'image', ...attachments[imgIdx++] });
      lastIdx = i + 2; // skip \uFFFC\u2009
      i++; // skip \u2009
    }
  }
  if (lastIdx < text.length) segments.push({ type: 'text', value: text.slice(lastIdx) });
  return (
    <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word', display: 'block', lineHeight: 1.5 }}>
      {segments.map((seg, idx) =>
        seg.type === 'text' ? (
          <span key={idx}>{seg.value}</span>
        ) : (
          <img
            key={idx}
            src={seg.url?.split('|')[0]}
            alt="inline"
            style={{
              display: 'inline-block',
              verticalAlign: 'middle',
              height: '1.5em',
              width: 'auto',
              margin: '0 4px',
              cursor: 'pointer'
            }}
            onClick={() => onImageClick(seg.url?.split('|')[1] || seg.url)}
          />
        )
      )}
    </Typography>
  );
}

const ChatInput = ({ onSend, scrollToTop }) => {
  const editorRef = useRef();
  const fileInputRef = useRef();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'image' or 'gif' or null
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  // Map blob URL to File object for reliable lookup
  const blobUrlToFile = useRef(new Map());

  // Track selection in editor
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      window._lastSelection = sel.getRangeAt(0).cloneRange();
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      // Insert image at cursor immediately
      editorRef.current.focus();
      insertImageAtCursor(url, file);
      // Clean up file input value so the same file can be selected again
      e.target.value = '';
    }
  };

  // Open dialog for URL input
  const handleUrlButton = () => {
    setUrlDialogOpen(true);
    setUrlInput("");
    setPreviewUrl(null);
    setPreviewType(null);
  };

  // Handle URL input change
  const handleUrlInputChange = (e) => {
    setUrlInput(e.target.value);
    if (/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(e.target.value)) {
      setPreviewUrl(e.target.value);
      setPreviewType(e.target.value.toLowerCase().endsWith('.gif') ? 'gif' : 'image');
    } else {
      setPreviewUrl(null);
      setPreviewType(null);
    }
  };

  // Insert preview image into editor
  const handleInsertPreview = (e) => {
    e && e.preventDefault();
    if (previewType && previewUrl) {
      // Move focus to editor before inserting
      editorRef.current.focus();
      // Insert at current cursor position
      setTimeout(() => {
        insertImageAtCursor(previewUrl);
      }, 0);
      setPreviewUrl(null);
      setPreviewType(null);
      setUrlDialogOpen(false);
      setUrlInput("");
    }
  };

  // Patch insertImageAtCursor to attach file object if available
  function insertImageAtCursor(src, fileObj) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = 'inline';
    img.style.display = 'inline-block';
    img.style.verticalAlign = 'middle';
    img.style.height = '1.5em';
    img.style.margin = '0 4px';
    if (fileObj && src.startsWith('blob:')) {
      blobUrlToFile.current.set(src, fileObj);
    }
    // Restore last selection if available
    if (window._lastSelection) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(window._lastSelection);
    }
    // Insert at caret
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.collapse(false);
    range.insertNode(img);
    // Move caret after image
    range.setStartAfter(img);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    editorRef.current.focus();
  }

  // Extract message as array of {type, value} segments
  const extractMessageSegments = () => {
    const segments = [];
    const childNodes = editorRef.current.childNodes;
    childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim() !== '') {
          segments.push({ type: 'text', value: node.textContent });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG') {
        if (node.src.startsWith('blob:')) {
          // Use the blobUrlToFile map to get the file
          segments.push({ type: 'image', url: node.src, file: blobUrlToFile.current.get(node.src) || null });
        } else {
          segments.push({ type: 'image', url: node.src });
        }
      }
    });
    return segments;
  };

  const handleSendClick = async () => {
    const segments = extractMessageSegments();
    if (!segments.length) return;
    await onSend(segments);
    if (editorRef.current) editorRef.current.innerHTML = '';
    scrollToTop();
  };

  return (
    <Paper sx={{ display: 'flex', alignItems: 'center', p: 1, mt: 1, flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <IconButton color="primary" component="span" onClick={() => fileInputRef.current.click()}>
          <PhotoCamera />
        </IconButton>
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleImageChange}
        />
        <IconButton color="primary" onClick={handleUrlButton} sx={{ ml: 1 }} aria-label="Paste Image URL">
          <LinkIcon />
        </IconButton>
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          sx={{ ml: 1, flex: 1, minHeight: 32, border: '1px solid #ccc', borderRadius: 1, px: 1, py: 0.5, outline: 'none', fontSize: 16, lineHeight: 1.5, maxHeight: 120, overflowY: 'auto' }}
          placeholder="Type a message..."
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendClick(); } }}
          onMouseUp={saveSelection}
          onKeyUp={saveSelection}
        />
        <IconButton color="primary" onClick={handleSendClick}>
          <SendIcon />
        </IconButton>
      </Box>
      {/* URL Dialog */}
      {urlDialogOpen && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, width: '100%', bgcolor: '#f9f9f9', borderRadius: 2, p: 2, boxShadow: 2 }}>
          <InputBase
            sx={{ flex: 1, mr: 2 }}
            placeholder="Paste image URL (jpg, png, gif, webp)"
            value={urlInput}
            onChange={handleUrlInputChange}
            autoFocus
          />
          {previewType && previewUrl && (
            <img src={previewUrl} alt="preview" style={{ maxHeight: 80, maxWidth: 120, borderRadius: 4, marginRight: 8 }} />
          )}
          <Button variant="contained" color="primary" onClick={handleInsertPreview} disabled={!previewType || !previewUrl} sx={{ mr: 1 }}>
            Insert
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => { setUrlDialogOpen(false); setPreviewUrl(null); setPreviewType(null); setUrlInput(""); }}>
            Cancel
          </Button>
        </Box>
      )}
    </Paper>
  );
};

// iOS-style floating action button and popup input
const IOSChatInput = ({ onSend, scrollToTop }) => {
  const [open, setOpen] = useState(false);
  const { authUser } = useContext(AuthContext);
  const editorRef = useRef();
  const fileInputRef = useRef();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const blobUrlToFile = useRef(new Map());

  // Track selection in editor
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      window._lastSelection = sel.getRangeAt(0).cloneRange();
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      editorRef.current.focus();
      insertImageAtCursor(url, file);
      e.target.value = '';
    }
  };

  const handleUrlButton = () => {
    setUrlDialogOpen(true);
    setUrlInput("");
    setPreviewUrl(null);
    setPreviewType(null);
  };

  const handleUrlInputChange = (e) => {
    setUrlInput(e.target.value);
    if (/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(e.target.value)) {
      setPreviewUrl(e.target.value);
      setPreviewType(e.target.value.toLowerCase().endsWith('.gif') ? 'gif' : 'image');
    } else {
      setPreviewUrl(null);
      setPreviewType(null);
    }
  };

  const handleInsertPreview = (e) => {
    e && e.preventDefault();
    if (previewType && previewUrl) {
      editorRef.current.focus();
      setTimeout(() => {
        insertImageAtCursor(previewUrl);
      }, 0);
      setPreviewUrl(null);
      setPreviewType(null);
      setUrlDialogOpen(false);
      setUrlInput("");
    }
  };

  function insertImageAtCursor(src, fileObj) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = 'inline';
    img.style.display = 'inline-block';
    img.style.verticalAlign = 'middle';
    img.style.height = '1.5em';
    img.style.margin = '0 4px';
    if (fileObj && src.startsWith('blob:')) {
      blobUrlToFile.current.set(src, fileObj);
    }
    if (window._lastSelection) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(window._lastSelection);
    }
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.collapse(false);
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    editorRef.current.focus();
  }

  // Extract message as array of {type, value} segments
  const extractMessageSegments = () => {
    const segments = [];
    const childNodes = editorRef.current.childNodes;
    childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim() !== '') {
          segments.push({ type: 'text', value: node.textContent });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG') {
        if (node.src.startsWith('blob:')) {
          segments.push({ type: 'image', url: node.src, file: blobUrlToFile.current.get(node.src) || null });
        } else {
          segments.push({ type: 'image', url: node.src });
        }
      }
    });
    return segments;
  };

  const handleSendClick = async () => {
    const segments = extractMessageSegments();
    if (!segments.length) return;
    await onSend(segments);
    if (editorRef.current) editorRef.current.innerHTML = '';
    scrollToTop();
    setOpen(false);
  };

  return (
    <>
      {/* iOS-style floating button only when inputOnTop is false */}
      {!open && authUser?.user && (
        <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 100 }}>
          <IconButton 
            color="primary" 
            sx={{ bgcolor: '#0a0a23', color: '#fff', boxShadow: 3, width: 48, height: 48, '&:hover': { bgcolor: '#1565c0' }, fontSize: 32 }} 
            onClick={() => setOpen(true)} 
            size="medium"
          >
            <AddCommentIcon fontSize="medium" sx={{ transform: 'scaleX(-1)' }} />
          </IconButton>
        </Box>
      )}
      {/* Modal input dialog logic (only when open) */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box 
          className="ios-chat-modal"
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0,
            width: '100vw', 
            bgcolor: '#fff', 
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            boxShadow: 24, 
            p: 3, 
            zIndex: 1300, 
            maxWidth: 480, 
            mx: 'auto',
            height: '44vh', // taller for web link preview
            minHeight: 280,
            border: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            transition: 'height 0.3s cubic-bezier(0.32,0.72,0.36,1)',
          }}
        >
          {/* Multi-line contentEditable input on top */}
          <Paper elevation={2} sx={{ minHeight: 56, maxHeight: 80, mt: 4, mb: 2, p: 1, borderRadius: 3, bgcolor: '#f7f7f7', border: '1.5px solid #e0e0e0', boxShadow: '0 1px 2px #0001', overflowY: 'auto', position: 'relative' }}>
            <Box
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              sx={{ minHeight: 40, maxHeight: 60, px: 2, py: 1, fontSize: 16, lineHeight: 1.6, outline: 'none', overflowY: 'auto', bgcolor: 'inherit', position: 'relative', zIndex: 2 }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendClick(); } }}
              onMouseUp={saveSelection}
              onKeyUp={saveSelection}
            />
          </Paper>
          {/* Action icons row below input */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton color="primary" component="span" onClick={() => fileInputRef.current.click()}>
                <PhotoCamera />
              </IconButton>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleImageChange}
              />
              <IconButton color="primary" onClick={handleUrlButton} aria-label="Paste Image URL">
                <LinkIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton color="primary" onClick={handleSendClick}>
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
          {/* Cancel icon at top right */}
          <IconButton 
            color="secondary" 
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          {/* URL Dialog */}
          {urlDialogOpen && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, width: '100%', bgcolor: '#f9f9f9', borderRadius: 2, p: 2, boxShadow: 2 }}>
              <InputBase
                sx={{ flex: 1, mr: 2 }}
                placeholder="Paste image URL (jpg, png, gif, webp)"
                value={urlInput}
                onChange={handleUrlInputChange}
                autoFocus
              />
              {previewType && previewUrl && (
                <img src={previewUrl} alt="preview" style={{ maxHeight: 80, maxWidth: 120, borderRadius: 4, marginRight: 8 }} />
              )}
              <Button variant="contained" color="primary" onClick={handleInsertPreview} disabled={!previewType || !previewUrl} sx={{ mr: 1 }}>
                Insert
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => { setUrlDialogOpen(false); setPreviewUrl(null); setPreviewType(null); setUrlInput(""); }}>
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

const PAGE_SIZE = 20;

const MediaTextView = ({ channelId, currentUserId, isOwner, inputOnTop = false }) => {
  const { authUser, authInstance, firestoreInstance, functionsInstance, storageInstance } = useContext(AuthContext);
  const navigate = useNavigate();
  const { config } = useContext(AppContext) || {};

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const [listenerActive, setListenerActive] = useState(true);
  const [atTop, setAtTop] = useState(true); // Track if user is at the top (latest page)
  const chatListRef = useRef();
  const unsubscribeRef = useRef(null);

  // Real-time listener for the first/latest page only
  useEffect(() => {
    if (!firestoreInstance || !channelId || !atTop) return;
    setLoading(true);
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setListenerActive(true);
    const q = query(
      collection(firestoreInstance, "channels", channelId, "messages"),
      orderBy("timestamp", "desc"),
      limit(PAGE_SIZE)
    );
    unsubscribeRef.current = onSnapshot(q, (snapshot) => {
      if (!listenerActive) return;
      const newMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(newMessages);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
      setLoading(false);
    });
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [firestoreInstance, channelId, listenerActive, atTop]);

  // Pagination: load older messages (not real-time)
  const fetchMoreMessages = useCallback(async () => {
    if (!firestoreInstance || !channelId || !lastDoc || !hasMore || isPaginating) return;
    setIsPaginating(true);
    setListenerActive(false); // Detach real-time listener after paginating
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    let q = query(
      collection(firestoreInstance, "channels", channelId, "messages"),
      orderBy("timestamp", "desc"),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );
    const snap = await getDocs(q);
    const moreMessages = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setMessages(prev => {
      // Append older messages to the end, de-duplicate by id
      const existingIds = new Set(prev.map(m => m.id));
      const uniqueMore = moreMessages.filter(m => !existingIds.has(m.id));
      return [...prev, ...uniqueMore];
    });
    setLastDoc(snap.docs[snap.docs.length - 1]);
    setHasMore(snap.docs.length === PAGE_SIZE);
    setIsPaginating(false);
    setAtTop(false); // Mark as not at top after paginating
  }, [firestoreInstance, channelId, lastDoc, hasMore, isPaginating]);

  // Remove old fetchMessages reference in useEffect. The real-time onSnapshot now handles initial loading, so fetchMessages is not needed.

  // Remove scroll-to-top on new message (own message)
  // This is now handled only in ChatList for correct iOS-like behavior

  // Ensure handleArchive is defined
  const handleArchive = async (messageId) => {
    if (!firestoreInstance) return;
    // Reference to the original message
    const sourceRef = doc(firestoreInstance, "channels", channelId, "messages", messageId);
    const snapshot = await getDoc(sourceRef);
    if (!snapshot.exists()) return;
    const data = snapshot.data();
    // Append archivedAt timestamp
    data.archivedAt = serverTimestamp();
    // Reference to the archived location
    const archiveRef = doc(firestoreInstance, "channels", channelId, "archived", messageId);
    // Batched write: set in archived, delete from messages
    const batch = writeBatch(firestoreInstance);
    batch.set(archiveRef, data);
    batch.delete(sourceRef);
    await batch.commit();
  };

  const handleSendMessage = async (segments) => {
    if (!firestoreInstance || !channelId) return;
    let text = '';
    let attachments = [];
    let cursor = 0;
    for (const seg of segments) {
      if (seg.type === 'text') {
        text += seg.value;
        cursor += seg.value.length;
      } else if (seg.type === 'image') {
        text += '\uFFFC\u2009';
        let url = seg.url;
        let fileType = 'image';
        if (seg.file) {
          // Only upload if seg.file exists (local file)
          const file = seg.file;
          const fileName = uuidv4();
          if (file.type && file.type.toLowerCase().includes('gif')) {
            fileType = 'gif';
            const storage = getStorage();
            const storagePath = `chat_images/${channelId}/${fileName}.gif`;
            const fileRef = storageRef(storage, storagePath);
            await uploadBytes(fileRef, file);
            url = await getDownloadURL(fileRef);
            // Try to get GIF dimensions (optional, fallback to 40x40)
            var width = 40, height = 40;
            try {
              const img = document.createElement('img');
              img.src = url;
              await new Promise((res, rej) => {
                img.onload = () => { width = img.width; height = img.height; res(); };
                img.onerror = res;
              });
            } catch {}
            attachments.push({ url, type: fileType, range: [cursor, 2], width, height });
            cursor += 2;
            continue;
          } else {
            // Static image: upload both full and thumbnail
            const storage = getStorage();
            // Full size
            const fullPath = `chat_images/${channelId}/${fileName}_full.jpg`;
            const fullRef = storageRef(storage, fullPath);
            await uploadBytes(fullRef, file);
            const fullUrl = await getDownloadURL(fullRef);
            // Thumbnail
            const thumbBlob = await createImageThumbnail(file, 500);
            const thumbPath = `chat_images/${channelId}/${fileName}_thumb.jpg`;
            const thumbRef = storageRef(storage, thumbPath);
            await uploadBytes(thumbRef, thumbBlob);
            const thumbUrl = await getDownloadURL(thumbRef);
            url = `${thumbUrl}|${fullUrl}`;
            fileType = 'image';
            // Get thumbnail dimensions
            let width = 40, height = 40;
            try {
              const img = document.createElement('img');
              img.src = thumbUrl;
              await new Promise((res, rej) => {
                img.onload = () => { width = img.width; height = img.height; res(); };
                img.onerror = res;
              });
            } catch {}
            attachments.push({ url, type: fileType, range: [cursor, 2], width, height });
            cursor += 2;
            continue;
          }
        } else if (url && url.toLowerCase().endsWith('.gif')) {
          fileType = 'gif';
          // Try to get GIF dimensions (optional, fallback to 40x40)
          let width = 40, height = 40;
          try {
            const img = document.createElement('img');
            img.src = url;
            await new Promise((res, rej) => {
              img.onload = () => { width = img.width; height = img.height; res(); };
              img.onerror = res;
            });
          } catch {}
          attachments.push({ url, type: fileType, range: [cursor, 2], width, height });
          cursor += 2;
          continue;
        } else if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          // For static image URLs, use the same URL for both thumb and full
          url = `${url}|${url}`;
          fileType = 'image';
          // Try to get image dimensions (optional, fallback to 40x40)
          let width = 40, height = 40;
          try {
            const img = document.createElement('img');
            img.src = url.split('|')[0];
            await new Promise((res, rej) => {
              img.onload = () => { width = img.width; height = img.height; res(); };
              img.onerror = res;
            });
          } catch {}
          attachments.push({ url, type: fileType, range: [cursor, 2], width, height });
          cursor += 2;
          continue;
        }
        // Only store direct URLs (no blob:)
        if (url && url.startsWith('blob:')) continue;
        attachments.push({ url, type: fileType, range: [cursor, 2] });
        cursor += 2;
      }
    }
    // Send message with text and attachments
    const msgData = {
      channelId,
      senderId: currentUserId,
      text,
      attachments,
      timestamp: serverTimestamp()
    };
    await addDoc(collection(firestoreInstance, "channels", channelId, "messages"), msgData);
  };

  //console.log('MediaTextView rendered');

  return (
    <>
      {/* Only render iOS-style floating button/modal when inputOnTop is false */}
      {!inputOnTop && (
        <IOSChatInput onSend={handleSendMessage} scrollToTop={() => chatListRef.current.scrollToTop()} />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 800, bgcolor: '#fff', width: '100%', maxWidth: '100%', m: 0 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, overflowY: 'hidden', position: 'relative', width: '100%', maxWidth: '100%', m: 0 }}>
          {inputOnTop && (
            <ChatInput onSend={handleSendMessage} scrollToTop={() => chatListRef.current.scrollToTop()} />
          )}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', borderRadius: 1, bgcolor: '#fff', p: 1, width: '100%', maxWidth: '100%', m: 0 }}>
            {loading && <CircularProgress size={24} sx={{ alignSelf: 'center', my: 2 }} />}
            <ChatList
              ref={chatListRef}
              messages={messages}
              currentUserId={currentUserId}
              isOwner={isOwner}
              onImageClick={url => { setModalImageUrl(url); setShowImageModal(true); }}
              onArchive={handleArchive}
              fetchMore={fetchMoreMessages}
              hasMore={hasMore}
              setAtTop={setAtTop}
            />
          </Box>
        </Box>
        <FullSizeImageModal open={showImageModal} imageUrl={modalImageUrl} onClose={() => setShowImageModal(false)} />
      </Box>
    </>
  );
};

export default MediaTextView;